import React, { useRef, useState } from "react";

import demoPic from "src/assets/png/userd-demo-pic.png";

import Cookies from "js-cookie";
import { BiMoon, BiSun } from "react-icons/bi";
import {
  CgChevronDown,
  CgChevronUp,
  CgHeadset,
  CgLogOut,
  CgProfile,
} from "react-icons/cg";
import { FaRedo } from "react-icons/fa";
import { HiChevronLeft } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import { ReactComponent as Logo } from "src/assets/svg/logo/badge.svg";
import { ReactComponent as LogoInline } from "src/assets/svg/logo/hypefit-inline.svg";
import { useAuth } from "src/contexts/auth";
import { useDialog } from "src/contexts/dialog-context";
import { useStudent } from "src/contexts/student";
import { twMerge } from "tailwind-merge";
import { useOnOutClick } from "../hooks";

function useIsSmall() {
  const [isSmall, setIsSmall] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    function handleResize() {
      setIsSmall(window.innerWidth < 768);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isSmall;
}

interface HeaderProps {
  photourl?: string;
  name?: string;
}

const DarkModeToggle: React.FC<{
  className?: string;
  isSmall?: boolean;
}> = ({ className = "", isSmall }) => {
  const dialog = useDialog();

  // Sync dark mode state
  React.useEffect(() => {
    setIsDarkMode(
      document.querySelector(".App")?.classList.contains("dark") ?? false,
    );
  }, []);

  function Toggle() {
    dialog.openDialog(
      "[Beta] Modo Escuro",
      "Esta função está em implementação. Gostaria de alterar o tema mesmo assim?",
      () => {
        const app = document.querySelector(".App");
        if (isDarkMode) {
          localStorage.setItem("darkMode", "false");
          app?.classList.remove("dark");
          setIsDarkMode(false);
        } else {
          localStorage.setItem("darkMode", "true");
          app?.classList.add("dark");
          setIsDarkMode(true);
        }
      },
    );
  }

  const [isDarkMode, setIsDarkMode] = useState(
    document.querySelector(".App")?.classList.contains("dark") ?? false,
  );

  if (!isSmall)
    return (
      <button
        onClick={Toggle}
        className={twMerge(
          "flex h-10 w-10 items-center justify-center rounded-full border text-sm font-medium text-black transition-all hover:bg-zinc-300 dark:bg-zinc-700 dark:text-white dark:shadow-zinc-700 dark:hover:bg-zinc-600",
          className,
        )}
        aria-label="Alternar modo escuro"
      >
        {isDarkMode ? <BiSun /> : <BiMoon />}
      </button>
    );

  return (
    <button
      onClick={Toggle}
      className="flex h-10 w-full items-center gap-2 rounded px-3 text-sm transition-colors hover:bg-zinc-50"
    >
      {isDarkMode ? <BiSun /> : <BiMoon />}
      <span>Modo {isDarkMode ? "Claro" : "Escuro"}</span>
    </button>
  );
};

const Header: React.FC<HeaderProps> = ({ photourl, name }) => {
  const [info, setInfo] = useState(false);

  const std = useStudent();
  const auth = useAuth();
  const nav = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  useOnOutClick(ref, () => setInfo(false));
  // useIsSmall hook

  const small = useIsSmall();

  function GoBack() {
    nav(-1);
  }

  function handleMenuBtn(callback?: () => void) {
    setInfo(false);
    if (callback) callback();
  }

  const adAccess = Cookies.get("ad@access");

  function redoAccess() {
    if (adAccess) {
      Cookies.set("@access", adAccess, { expires: 30 });
      Cookies.remove("ad@access");
      window.location.href = "/adtools";
      window.location.reload();
    }
  }

  return (
    <header className="relative flex h-16 w-full items-center justify-between px-2 py-2 dark:bg-transparent">
      {/* Left Section */}
      <div className="flex items-center gap-3">
        {window.location.pathname.startsWith("/dash") ? (
          small ? (
            <Logo className="h-8 fill-black dark:fill-white" />
          ) : (
            <LogoInline className="h-8 fill-black dark:fill-white" />
          )
        ) : (
          <button onClick={GoBack} className="flex items-center">
            <HiChevronLeft className="dark:text-white" size={32} />
          </button>
        )}
        {std.student && (
          <div className="ml-2 flex items-center gap-2">
            <img
              className="h-10 w-10 rounded-full bg-zinc-300"
              src={std.student.photourl ?? demoPic}
              alt="user"
            />
            <div className="pl-2 dark:text-white">
              <p className="font-medium">{std.student.name}</p>
              {adAccess && (
                <button
                  className="ml-2 flex items-center gap-1 rounded-full bg-primary-400 px-2 py-1 text-xs text-white"
                  onClick={redoAccess}
                >
                  <FaRedo />
                  <span>ADTOOLS</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-2">
        {!small && <DarkModeToggle className="hidden md:flex" />}
        <div
          onClick={() => !info && setInfo(true)}
          className="flex h-10 cursor-pointer items-center gap-2 rounded-full px-2 transition-colors dark:border-zinc-700 dark:bg-transparent"
        >
          <div className="flex flex-row items-center">
            <img
              className="h-10 w-10 rounded-full border md:mr-2"
              src={photourl ?? demoPic}
              alt="user avatar"
            />
            <div className="hidden flex-1 select-none flex-row items-center gap-2 dark:text-white md:flex">
              {!small && <p className="text-sm">{name}</p>}
              {!info ? <CgChevronDown /> : <CgChevronUp />}
            </div>
          </div>
        </div>
      </div>

      {/* Dropdown */}
      {info && (
        <div
          ref={ref}
          className="absolute right-4 top-16 z-10 w-[300px] flex-col justify-between gap-1 rounded-xl border border-zinc-150 bg-white p-2 shadow-lg"
        >
          <div className="hidden flex-col pb-3">
            <p className="text-sm font-medium">{auth.user?.name}</p>
            <p className="text-xs text-zinc-500">{auth.user?.email}</p>
          </div>
          {small && <DarkModeToggle isSmall />}
          <button
            onClick={() => handleMenuBtn(() => nav("/settings"))}
            className="flex h-10 w-full items-center gap-2 rounded px-3 text-sm transition-colors hover:bg-zinc-50"
          >
            <CgProfile size={20} className="text-zinc-500" />
            <span>Editar Perfil</span>
          </button>
          <button
            onClick={() =>
              window.open(
                "https://chat.whatsapp.com/C6IiOC1v4kg9AXy2tR9AQk",
                "_blank",
              )
            }
            className="flex h-10 w-full items-center gap-2 rounded px-3 text-sm transition-colors hover:bg-zinc-50"
          >
            <CgHeadset size={20} className="text-zinc-500" />
            <span>Suporte</span>
          </button>
          <button
            onClick={() => handleMenuBtn(() => auth.Logout())}
            className="flex h-10 w-full items-center gap-2 rounded px-3 text-sm transition-colors hover:bg-zinc-50"
          >
            <CgLogOut size={20} className="text-red-500" />
            <span>Logout</span>
          </button>
        </div>
      )}
    </header>
  );
};

export default Header;
